import React from "react";
import { format } from "date-fns";
import { Clock, AlertTriangle } from "lucide-react";
import { ThemedSelect } from "@/components/ui/ThemedSelect";

/**
 * TimeWindowsSection — two ThemedSelect dropdowns for 1-hour delivery and
 * pickup windows. Delivery slots are pre-filtered by the caller (same-day
 * lead-time logic lives in the parent so we can display a friendly
 * "fully booked today" placeholder without re-computing it here).
 */
export const TimeWindowsSection = ({
  range,
  deliverySlot,
  onDeliverySlotChange,
  pickupSlot,
  onPickupSlotChange,
  deliverySlotsFiltered,
  pickupSlots,
  isSameDayDelivery,
  sameDayLeadHours = 6,
  isWithin24HoursSelected = false,
  isOffHoursBooking = false,
}) => (
  <div className="mt-6 pt-6 border-t border-[#FFD166]/10" data-testid="trip-setup-times-section">
    <label className="eyebrow flex items-center gap-2 mb-3">
      <Clock className="w-3.5 h-3.5 text-[#FFD166]" /> 3 · Delivery &amp; pickup windows
      {deliverySlot && pickupSlot && <span className="text-emerald-400 text-[11px]" data-testid="times-step-check">✓</span>}
    </label>

    {isOffHoursBooking && (
      <div
        className="mb-3 rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-3 flex gap-3 items-start"
        data-testid="off-hours-booking-notice"
      >
        <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
        <p className="text-xs text-orange-100 leading-relaxed">
          <span className="font-semibold text-orange-300">Booking outside business hours (9 PM – 7 AM).</span>{" "}
          We may not see this right away. All bookings placed overnight will be reviewed first thing in the morning —
          we'll reach out to confirm your delivery window or arrange an adjusted time.
        </p>
      </div>
    )}

    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <div className="text-[11px] text-[#B9C8D5] mb-1">Delivery on {format(range.from, "MMM d")}</div>
        <ThemedSelect
          value={deliverySlot}
          onChange={onDeliverySlotChange}
          placeholder={isSameDayDelivery && deliverySlotsFiltered.length === 0 ? "Fully booked today — try tomorrow" : "Choose 1-hour window…"}
          options={deliverySlotsFiltered.map((s) => ({ value: s.id, label: s.label }))}
          data-testid="trip-setup-delivery-slot"
        />
        {isSameDayDelivery && (
          <p className="text-[10px] text-[#FFD166]/80 mt-1">Same-day requires {sameDayLeadHours}-hour lead time — earlier windows are hidden.</p>
        )}
      </div>
      <div>
        <div className="text-[11px] text-[#B9C8D5] mb-1">Pickup on {format(range.to, "MMM d")}</div>
        <ThemedSelect
          value={pickupSlot}
          onChange={onPickupSlotChange}
          placeholder="Choose 1-hour window…"
          options={(pickupSlots || []).map((s) => ({ value: s.id, label: s.label }))}
          data-testid="trip-setup-pickup-slot"
        />
      </div>
    </div>

    {isWithin24HoursSelected && (
      <div
        className="mt-3 rounded-lg border border-amber-400/40 bg-amber-500/10 px-4 py-3 flex gap-3 items-start"
        data-testid="last-minute-not-guaranteed-notice"
      >
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-100 leading-relaxed">
          <span className="font-semibold text-amber-300">Last-minute delivery — not guaranteed.</span>{" "}
          We'll make every effort to meet your selected window, but we may not be able to accommodate this last-minute request.
          If we can't make it at your chosen time, we'll reach out to coordinate an adjusted delivery window.
        </p>
      </div>
    )}
  </div>
);

export default TimeWindowsSection;
